// Renders an SEO content page (compare or blog) from content-as-data. Same visual
// world as the rest of the site (PageShell frame, crw-prose, signal accents).
import { useEffect, type CSSProperties, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { PageShell, MetaChip } from '../pages/shell';
import { CRWIcon } from '../components/icons';
import type { SeoPage, Section, Mark as MarkKind } from './content.data.mjs';

const H2: CSSProperties = { margin: '0 0 12px', fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 700, letterSpacing: 'var(--tracking-heading)', color: 'var(--fg-1)' };
const PROSE_WRAP: CSSProperties = { maxWidth: 760, margin: '0 auto' };

// inline **bold** and [text](/path) → nodes
function inline(text: string): ReactNode[] {
  const out: ReactNode[] = [];
  const re = /\*\*([^*]+)\*\*|\[([^\]]+)\]\(([^)]+)\)/g;
  let last = 0; let key = 0; let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    if (m[1] !== undefined) {
      out.push(<strong key={key++} style={{ color: 'var(--fg-1)', fontWeight: 600 }}>{m[1]}</strong>);
    } else {
      const url = m[3];
      out.push(url.startsWith('/')
        ? <Link key={key++} to={url} className="crw-ilink">{m[2]}</Link>
        : <a key={key++} href={url} target="_blank" rel="noreferrer" className="crw-ilink">{m[2]}</a>);
    }
    last = re.lastIndex;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

function Prose({ paras, bullets }: { paras: string[]; bullets?: string[] }) {
  return (
    <div className="crw-prose">
      {paras.map((p, i) => <p key={i} style={{ margin: '0 0 14px', fontSize: 15.5, lineHeight: 1.72, color: 'var(--fg-2)' }}>{inline(p)}</p>)}
      {bullets?.length ? (
        <ul style={{ margin: '2px 0 14px', paddingLeft: 20, color: 'var(--fg-2)' }}>
          {bullets.map((b, i) => <li key={i} style={{ margin: '0 0 8px', fontSize: 15, lineHeight: 1.6 }}>{inline(b)}</li>)}
        </ul>
      ) : null}
    </div>
  );
}

function ArticleSection({ s }: { s: Section }) {
  return (
    <section style={{ marginTop: 32 }}>
      <h2 style={H2}>{s.h2}</h2>
      <Prose paras={s.paras} bullets={s.bullets} />
    </section>
  );
}

function Mark({ m, us }: { m: MarkKind; us?: boolean }) {
  const color = us ? 'var(--signal-400)' : m === 'yes' ? 'var(--positive)' : m === 'part' ? 'var(--warning)' : 'var(--fg-3)';
  const d = m === 'yes' ? 'check' : m === 'part' ? 'minus' : 'x';
  return <span style={{ color, display: 'inline-flex', flex: '0 0 auto', marginTop: 1 }}><CRWIcon d={d} size={13} sw={2.6} /></span>;
}

const ROW: CSSProperties = { display: 'grid', gridTemplateColumns: '1.05fr 1.3fr 1.3fr', alignItems: 'start' };
const CRIT: CSSProperties = { padding: '13px 16px', fontSize: 13, fontWeight: 500, color: 'var(--fg-1)' };
const VAL: CSSProperties = { padding: '13px 16px', fontSize: 13, lineHeight: 1.45, color: 'var(--fg-2)', display: 'flex', gap: 8, alignItems: 'flex-start' };

function CompareTable({ comparison }: { comparison: NonNullable<SeoPage['comparison']> }) {
  return (
    <div style={{ marginTop: 34 }}>
      <div className="crw-cmp-hint" style={{ fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--fg-3)', marginBottom: 8 }}>Swipe to compare →</div>
      <div className="crw-cmp-scroll" tabIndex={0} role="group" aria-label={`ClipRadar vs ${comparison.them} comparison`} style={{ overflowX: 'auto', border: '1px solid var(--line-2)', borderRadius: 12, background: 'var(--surface-card)' }}>
        <div style={{ minWidth: 560 }}>
          <div style={{ ...ROW, background: 'var(--surface-raised)' }}>
            <div style={{ ...CRIT, color: 'var(--fg-3)', fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Head to head</div>
            <div style={{ ...VAL, fontWeight: 600, color: 'var(--signal-300)', background: 'var(--signal-dim)' }}>ClipRadar</div>
            <div style={{ ...VAL, fontWeight: 600, color: 'var(--fg-1)' }}>{comparison.them}</div>
          </div>
          {comparison.criteria.map((c, i) => (
            <div key={i} style={{ ...ROW, borderTop: '1px solid var(--line-1)' }}>
              <div style={CRIT}>{c.label}</div>
              <div style={{ ...VAL, background: 'rgba(74,227,255,0.045)' }}><Mark m={c.usMark} us /><span>{c.us}</span></div>
              <div style={VAL}><Mark m={c.themMark} /><span>{c.them}</span></div>
            </div>
          ))}
        </div>
      </div>
      <p style={{ margin: '10px 2px 0', fontSize: 11.5, fontFamily: 'var(--font-mono)', color: 'var(--fg-3)' }}>
        Based on {comparison.them}’s public pages &amp; pricing, {comparison.asOf} — check their current details.
      </p>
    </div>
  );
}

function Faq({ faq }: { faq: [string, string][] }) {
  return (
    <section style={{ marginTop: 42 }}>
      <h2 style={H2}>Frequently asked questions</h2>
      <div style={{ borderTop: '1px solid var(--line-1)' }}>
        {faq.map(([q, a], i) => (
          <div key={i} style={{ borderBottom: '1px solid var(--line-1)', padding: '17px 0' }}>
            <h3 style={{ margin: '0 0 7px', fontSize: 15.5, fontWeight: 600, color: 'var(--fg-1)' }}>{q}</h3>
            <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.7, color: 'var(--fg-3)' }}>{inline(a)}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function Cta({ cta }: { cta: SeoPage['cta'] }) {
  return (
    <div style={{ marginTop: 46, padding: '30px 28px', borderRadius: 14, border: '1px solid rgba(74,227,255,0.22)', background: 'linear-gradient(180deg, rgba(74,227,255,0.06), rgba(139,124,255,0.03))', textAlign: 'center' }}>
      <h2 style={{ ...H2, marginBottom: 8 }}>{cta.h2}</h2>
      <p style={{ margin: '0 auto 18px', maxWidth: 460, fontSize: 14.5, lineHeight: 1.6, color: 'var(--fg-2)' }}>{cta.sub}</p>
      <Link to={cta.href} className="crw-cta-btn">{cta.button}</Link>
    </div>
  );
}

export function SeoPageView({ page }: { page: SeoPage }) {
  // client-side title on SPA navigation (crawlers get the prerendered head)
  useEffect(() => {
    const prev = document.title;
    document.title = page.title;
    return () => { document.title = prev; };
  }, [page.title]);

  const parentLabel = page.kind === 'compare' ? 'Compare' : 'Blog';
  const dateChip = page.updated ? `Updated · ${page.updated}` : `Published · ${page.datePublished}`;

  return (
    <PageShell
      kicker={page.hero.eyebrow}
      title={page.hero.h1}
      sub={page.hero.dek}
      wide={page.kind === 'compare'}
      meta={<><MetaChip>{dateChip}</MetaChip>{page.competitor ? <MetaChip>vs {page.competitor}</MetaChip> : null}</>}
    >
      {/* prose is capped to a comfortable ~760px measure; the comparison table breaks out wide */}
      <div style={PROSE_WRAP}>
        <nav style={{ marginBottom: 22, fontSize: 12.5, fontFamily: 'var(--font-mono)', color: 'var(--fg-3)', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Link to="/" className="crw-crumb">Home</Link>
          <span aria-hidden="true">/</span>
          <Link to={page.kind === 'compare' ? '/compare' : '/blog'} className="crw-crumb">{parentLabel}</Link>
        </nav>
        {page.intro.length ? <Prose paras={page.intro} /> : null}
      </div>

      {page.comparison ? <CompareTable comparison={page.comparison} /> : null}

      <div style={PROSE_WRAP}>
        {page.sections.map((s, i) => <ArticleSection key={i} s={s} />)}
        {page.verdict ? <section style={{ marginTop: 32 }}><h2 style={H2}>{page.verdict.h2}</h2><Prose paras={page.verdict.paras} /></section> : null}
        {page.faq.length ? <Faq faq={page.faq} /> : null}
        <Cta cta={page.cta} />
        {page.related?.length ? (
          <div style={{ marginTop: 40, paddingTop: 18, borderTop: '1px solid var(--line-1)' }}>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--fg-3)' }}>Related</span>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, marginTop: 10 }}>
              {page.related.map((r) => <Link key={r.to} to={r.to} className="crw-ilink">{r.label}</Link>)}
            </div>
          </div>
        ) : null}
      </div>
    </PageShell>
  );
}
